import React, { useState } from 'react';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Select,
  SelectOption,
  Spinner,
  Switch,
  Textarea,
} from '../../../components/ui';
import { BVBrandVoice, BVBrandVoiceDraft, BVSafeCompareStrictness } from '../types';

interface BrandVoiceManagerProps {
  items: BVBrandVoice[];
  isLoading?: boolean;
  onCreate: (payload: BVBrandVoiceDraft) => Promise<void>;
  onUpdate: (configId: number, payload: BVBrandVoiceDraft) => Promise<void>;
  onDelete: (configId: number) => Promise<void>;
}

interface BrandVoiceFormState {
  name: string;
  toneDescriptorsText: string;
  terminologyRulesText: string;
  safe_compare_enabled: boolean;
  safe_compare_strictness: BVSafeCompareStrictness;
  citation_min_threshold: number;
  custom_system_prompt: string;
  is_default: boolean;
}

const STRICTNESS_OPTIONS: Array<{ value: BVSafeCompareStrictness; label: string }> = [
  { value: 'strict', label: 'Strict' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'relaxed', label: 'Relaxed' },
];

const EMPTY_FORM: BrandVoiceFormState = {
  name: '',
  toneDescriptorsText: '',
  terminologyRulesText: '{}',
  safe_compare_enabled: true,
  safe_compare_strictness: 'moderate',
  citation_min_threshold: 10,
  custom_system_prompt: '',
  is_default: false,
};

function formatToneDescriptors(value: string[]): string {
  return value.join(', ');
}

function createFormState(config?: BVBrandVoice): BrandVoiceFormState {
  if (!config) {
    return EMPTY_FORM;
  }

  return {
    name: config.name,
    toneDescriptorsText: formatToneDescriptors(config.tone_descriptors),
    terminologyRulesText: JSON.stringify(config.terminology_rules ?? {}, null, 2),
    safe_compare_enabled: config.safe_compare_enabled,
    safe_compare_strictness: config.safe_compare_strictness as BVSafeCompareStrictness,
    citation_min_threshold: config.citation_min_threshold,
    custom_system_prompt: config.custom_system_prompt ?? '',
    is_default: config.is_default,
  };
}

function parseToneDescriptors(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,]+/)
        .map(item => item.trim())
        .filter(Boolean),
    ),
  );
}

export function BrandVoiceManager({
  items,
  isLoading = false,
  onCreate,
  onUpdate,
  onDelete,
}: BrandVoiceManagerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<BVBrandVoice | null>(null);
  const [form, setForm] = useState<BrandVoiceFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const openCreateModal = () => {
    setEditingConfig(null);
    setForm(createFormState());
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (config: BVBrandVoice) => {
    setEditingConfig(config);
    setForm(createFormState(config));
    setFormError(null);
    setIsModalOpen(true);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setEditingConfig(null);
    setForm(createFormState());
    setFormError(null);
  };

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    resetModal();
  };

  const handleSubmit = async () => {
    const name = form.name.trim();
    if (!name) {
      setFormError('Configuration name is required.');
      return;
    }

    let terminologyRules: Record<string, unknown> = {};
    try {
      const parsedRules = form.terminologyRulesText.trim()
        ? JSON.parse(form.terminologyRulesText)
        : {};
      if (!parsedRules || typeof parsedRules !== 'object' || Array.isArray(parsedRules)) {
        setFormError('Terminology rules must be a JSON object.');
        return;
      }
      terminologyRules = parsedRules as Record<string, unknown>;
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Terminology rules must be valid JSON.');
      return;
    }

    if (
      !Number.isFinite(form.citation_min_threshold)
      || form.citation_min_threshold < 1
      || form.citation_min_threshold > 1000
    ) {
      setFormError('Citation threshold must be between 1 and 1000.');
      return;
    }

    const payload: BVBrandVoiceDraft = {
      name,
      tone_descriptors: parseToneDescriptors(form.toneDescriptorsText),
      terminology_rules: terminologyRules,
      safe_compare_enabled: form.safe_compare_enabled,
      safe_compare_strictness: form.safe_compare_strictness,
      citation_min_threshold: form.citation_min_threshold,
      custom_system_prompt: form.custom_system_prompt.trim() || null,
      is_default: form.is_default,
    };

    setIsSubmitting(true);
    setFormError(null);
    try {
      if (editingConfig) {
        await onUpdate(editingConfig.id, payload);
      } else {
        await onCreate(payload);
      }
      resetModal();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to save brand voice settings.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (config: BVBrandVoice) => {
    if (!window.confirm(`Delete brand voice preset "${config.name}"?`)) {
      return;
    }

    setDeletingId(config.id);
    try {
      await onDelete(config.id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Panel>
        <PanelHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <PanelTitle>Brand Voice & Guardrails</PanelTitle>
              <PanelDescription>
                Tune default tone, safe-compare posture, terminology, and citation thresholds for BV outputs.
              </PanelDescription>
            </div>
            <Button onClick={openCreateModal}>
              <PlusIcon className="mr-2 h-4 w-4" />
              New preset
            </Button>
          </div>
        </PanelHeader>
        <PanelBody className="space-y-4">
          {isLoading ? (
            <div className="flex min-h-[160px] items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <Alert variant="info" title="No brand voice presets yet">
              Create a preset to keep Studio generations aligned with tone, compliance, and safe-compare rules.
            </Alert>
          ) : (
            items.map(item => (
              <div
                key={item.id}
                className="rounded-2xl border border-gray-200 p-4 dark:border-dark-border"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-charcoal dark:text-gray-100">{item.name}</p>
                      {item.is_default ? <Badge variant="brand">Default</Badge> : null}
                      <Badge variant={item.safe_compare_enabled ? 'success' : 'secondary'}>
                        {item.safe_compare_enabled ? 'Safe compare on' : 'Safe compare off'}
                      </Badge>
                      <Badge variant="secondary">{item.safe_compare_strictness}</Badge>
                    </div>

                    {item.tone_descriptors.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {item.tone_descriptors.map(descriptor => (
                          <Badge key={descriptor} variant="secondary">
                            {descriptor}
                          </Badge>
                        ))}
                      </div>
                    ) : null}

                    <div className="grid gap-2 text-xs text-gray-500 dark:text-gray-400 sm:grid-cols-2">
                      <p>Citation threshold: {item.citation_min_threshold} reviews</p>
                      <p>Terminology rules: {Object.keys(item.terminology_rules ?? {}).length}</p>
                    </div>

                    {item.custom_system_prompt ? (
                      <p className="max-w-3xl text-sm text-gray-500 dark:text-gray-400">
                        {item.custom_system_prompt}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditModal(item)}>
                      <PencilSquareIcon className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={deletingId === item.id}
                      onClick={() => void handleDelete(item)}
                    >
                      <TrashIcon className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </PanelBody>
      </Panel>

      <Modal open={isModalOpen} onClose={closeModal}>
        <ModalBackdrop />
        <ModalContent size="xl">
          <ModalHeader>
            <ModalTitle>{editingConfig ? 'Edit brand voice preset' : 'Create brand voice preset'}</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-5">
            {formError ? (
              <Alert variant="error" title="Unable to save preset">
                {formError}
              </Alert>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bv-brand-voice-name">Preset name</Label>
                <Input
                  id="bv-brand-voice-name"
                  value={form.name}
                  onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
                  placeholder="Retail-safe proof points"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bv-brand-voice-threshold">Citation threshold</Label>
                <Input
                  id="bv-brand-voice-threshold"
                  type="number"
                  min={1}
                  max={1000}
                  value={form.citation_min_threshold}
                  onChange={event =>
                    setForm(current => ({
                      ...current,
                      citation_min_threshold: Number(event.target.value || 0),
                    }))
                  }
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bv-brand-voice-tone">Tone descriptors</Label>
                <Input
                  id="bv-brand-voice-tone"
                  value={form.toneDescriptorsText}
                  onChange={event =>
                    setForm(current => ({ ...current, toneDescriptorsText: event.target.value }))
                  }
                  placeholder="confident, concise, shopper-first"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label>Compare strictness</Label>
                <Select
                  value={form.safe_compare_strictness}
                  onValueChange={value =>
                    setForm(current => ({
                      ...current,
                      safe_compare_strictness: value as BVSafeCompareStrictness,
                    }))
                  }
                >
                  {STRICTNESS_OPTIONS.map(option => (
                    <SelectOption key={option.value} value={option.value}>
                      {option.label}
                    </SelectOption>
                  ))}
                </Select>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-gray-200 px-3 py-3 dark:border-dark-border">
              <Switch
                checked={form.safe_compare_enabled}
                onCheckedChange={checked => setForm(current => ({ ...current, safe_compare_enabled: checked }))}
                disabled={isSubmitting}
              />
              <div>
                <p className="text-sm font-medium text-charcoal dark:text-gray-100">Enable safe compare mode</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Keep comparative phrasing conservative unless the evidence threshold and claim support are met.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 px-3 py-3 dark:border-dark-border">
              <Checkbox
                checked={form.is_default}
                onChange={event => setForm(current => ({ ...current, is_default: event.target.checked }))}
                disabled={isSubmitting}
                label="Use as default preset"
                description="New Studio drafts will start with this preset unless another one is selected explicitly."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bv-brand-voice-terminology">Terminology rules (JSON)</Label>
              <Textarea
                id="bv-brand-voice-terminology"
                value={form.terminologyRulesText}
                onChange={event =>
                  setForm(current => ({ ...current, terminologyRulesText: event.target.value }))
                }
                rows={6}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bv-brand-voice-prompt">Custom system prompt (optional)</Label>
              <Textarea
                id="bv-brand-voice-prompt"
                value={form.custom_system_prompt}
                onChange={event =>
                  setForm(current => ({ ...current, custom_system_prompt: event.target.value }))
                }
                rows={5}
                placeholder="Add extra writing guardrails or approved framing."
                disabled={isSubmitting}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : editingConfig ? 'Save changes' : 'Create preset'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default BrandVoiceManager;
